import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, ExternalLink, MapPin, Pencil, Trash2, Video } from 'lucide-react';
import { useMeeting, useDeleteMeeting, useUpdateMeeting } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    ConfirmDialog,
    DetailRow,
    ErrorState,
    FormField,
    FormGrid,
    Input,
    ListSkeleton,
    PageHeader,
    Select,
    Skeleton,
    Textarea,
} from '../components/ui';

const MeetingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { data: meeting, isLoading, isError } = useMeeting(id);
    const deleteMeetingMutation = useDeleteMeeting();
    const updateMeetingMutation = useUpdateMeeting();

    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [formData, setFormData] = useState(null);

    useEffect(() => {
        if (meeting) {
            setFormData({
                title: meeting.title,
                meeting_date: meeting.meeting_date.split('T')[0],
                start_time: meeting.start_time,
                end_time: meeting.end_time,
                description: meeting.description || '',
                meeting_link: meeting.meeting_link || '',
                venue: meeting.venue || '',
                meeting_type: meeting.meeting_type || 'face to face',
            });
        }
    }, [meeting]);

    const handleDelete = async () => {
        try {
            await deleteMeetingMutation.mutateAsync(id);
            setIsDeleteOpen(false);
            navigate('/meetings');
        } catch (error) {
            console.error(error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await updateMeetingMutation.mutateAsync({ id, data: formData });
            setIsEditing(false);
        } catch (error) {
            console.error(error);
        }
    };

    if (isLoading) {
        return (
            <>
                <Skeleton w={240} h={26} className="mb-5" />
                <Card>
                    <CardBody>
                        <ListSkeleton rows={4} />
                    </CardBody>
                </Card>
            </>
        );
    }

    if (isError || !meeting) {
        return (
            <Card>
                <ErrorState
                    title="Meeting not found"
                    description="It may have been cancelled, or you may not have access to it."
                    onRetry={() => navigate('/meetings')}
                />
            </Card>
        );
    }

    const isCreator = user?.id === meeting.created_by;
    const adminRoles = ['Manager', 'Head of Department', 'Developer'];
    const isGlobalAdmin = user && adminRoles.includes(user.role);
    const canManage = isCreator || isGlobalAdmin;
    const isVirtual = meeting.meeting_type === 'virtual';

    return (
        <div className="max-w-4xl">
            <PageHeader
                title={meeting.title}
                breadcrumbs={[{ label: 'Meetings', to: '/meetings' }, { label: 'Details' }]}
                actions={
                    canManage &&
                    !isEditing && (
                        <>
                            <Button icon={Pencil} onClick={() => setIsEditing(true)}>
                                Edit
                            </Button>
                            <Button variant="danger" icon={Trash2} onClick={() => setIsDeleteOpen(true)}>
                                Delete
                            </Button>
                        </>
                    )
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant={isVirtual ? 'info' : 'neutral'}>
                        {isVirtual ? 'Virtual' : 'Face to face'}
                    </Badge>
                    <span className="text-label text-ink-600">
                        Registered by {meeting.first_name} {meeting.last_name}
                    </span>
                </div>
            </PageHeader>

            {isEditing && formData ? (
                <Card>
                    <CardHeader title="Edit meeting" />
                    <form onSubmit={handleUpdate}>
                        <CardBody className="space-y-4">
                            <FormField label="Title" required htmlFor="md-title">
                                <Input
                                    id="md-title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                />
                            </FormField>

                            <FormGrid cols={3}>
                                <FormField label="Date" required htmlFor="md-date">
                                    <Input
                                        id="md-date"
                                        type="date"
                                        name="meeting_date"
                                        value={formData.meeting_date}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormField>
                                <FormField label="Start time" required htmlFor="md-start">
                                    <Input
                                        id="md-start"
                                        type="time"
                                        name="start_time"
                                        value={formData.start_time}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormField>
                                <FormField label="End time" required htmlFor="md-end">
                                    <Input
                                        id="md-end"
                                        type="time"
                                        name="end_time"
                                        value={formData.end_time}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormField>
                            </FormGrid>

                            <FormGrid>
                                <FormField label="Meeting type" htmlFor="md-type">
                                    <Select
                                        id="md-type"
                                        name="meeting_type"
                                        value={formData.meeting_type}
                                        onChange={handleChange}
                                    >
                                        <option value="face to face">Face to face</option>
                                        <option value="virtual">Virtual</option>
                                    </Select>
                                </FormField>

                                {formData.meeting_type === 'virtual' ? (
                                    <FormField label="Meeting link" htmlFor="md-link">
                                        <Input
                                            id="md-link"
                                            type="url"
                                            name="meeting_link"
                                            value={formData.meeting_link}
                                            onChange={handleChange}
                                            placeholder="https://…"
                                        />
                                    </FormField>
                                ) : (
                                    <FormField label="Venue" htmlFor="md-venue">
                                        <Input
                                            id="md-venue"
                                            name="venue"
                                            value={formData.venue}
                                            onChange={handleChange}
                                            placeholder="e.g. Conference Room A"
                                        />
                                    </FormField>
                                )}
                            </FormGrid>

                            <FormField label="Agenda" htmlFor="md-description">
                                <Textarea
                                    id="md-description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                />
                            </FormField>
                        </CardBody>

                        <CardFooter className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setIsEditing(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={updateMeetingMutation.isPending ?? updateMeetingMutation.isLoading}
                            >
                                Save changes
                            </Button>
                        </CardFooter>
                    </form>
                </Card>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                    <Card className="lg:col-span-2">
                        <CardHeader title="Agenda" />
                        <CardBody>
                            {meeting.description ? (
                                <p className="text-md text-ink-700 whitespace-pre-wrap leading-relaxed max-w-[75ch]">
                                    {meeting.description}
                                </p>
                            ) : (
                                <p className="text-base text-ink-500">
                                    No agenda was recorded for this meeting.
                                </p>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader title="Details" />
                        <CardBody className="divide-y divide-[var(--divider-color)] py-2">
                            <DetailRow label="Date">
                                <span className="inline-flex items-center gap-1.5">
                                    <CalendarDays size={13} className="text-ink-400" aria-hidden="true" />
                                    {new Date(`${meeting.meeting_date}T12:00:00`).toLocaleDateString(undefined, {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric',
                                    })}
                                </span>
                            </DetailRow>

                            <DetailRow label="Time">
                                <span className="tabular">
                                    {meeting.start_time?.slice(0, 5)} – {meeting.end_time?.slice(0, 5)}
                                </span>
                            </DetailRow>

                            <DetailRow label={isVirtual ? 'Link' : 'Venue'}>
                                {isVirtual ? (
                                    meeting.meeting_link ? (
                                        <a
                                            href={meeting.meeting_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-secondary-600 hover:text-secondary-700 transition-colors duration-150 break-all"
                                        >
                                            <Video size={13} aria-hidden="true" className="shrink-0" />
                                            Join meeting
                                            <ExternalLink size={11} aria-hidden="true" className="shrink-0" />
                                        </a>
                                    ) : (
                                        <span className="text-muted">No link provided</span>
                                    )
                                ) : (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={13} className="text-ink-400" aria-hidden="true" />
                                        {meeting.venue || 'To be confirmed'}
                                    </span>
                                )}
                            </DetailRow>

                            <DetailRow label="Registered by">
                                <span className="flex items-center gap-2">
                                    <Avatar name={`${meeting.first_name} ${meeting.last_name}`} size="sm" />
                                    {meeting.first_name} {meeting.last_name}
                                </span>
                            </DetailRow>
                        </CardBody>
                    </Card>
                </div>
            )}

            <ConfirmDialog
                isOpen={isDeleteOpen}
                title="Delete meeting"
                message="This removes the meeting from the shared calendar. This cannot be undone."
                confirmText="Delete meeting"
                variant="danger"
                isLoading={deleteMeetingMutation.isPending}
                onConfirm={handleDelete}
                onClose={() => setIsDeleteOpen(false)}
            />
        </div>
    );
};

export default MeetingDetail;
